import { matchPath, useLocation } from 'react-router';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useUserProfile from './UserProfileHook';
import { SidebarMenuItem } from '../components/Sidebar/SidebarMenuItem';
import { isRoleBorrowerAgent, isRoleFinanceAgent, isRoleOpsAgent } from '../constants/roles';
import {
  ProductRoutesBasePath, rootPath,
} from '../constants/routes';
import SidebarConfig from '../components/Sidebar/config';

function getSidebarMenu(productId: string, roles: string[]): ISidebarMenu {
  const productSidebarConfig = SidebarConfig[productId];

  if (productSidebarConfig) {
    return productSidebarConfig(roles);
  }

  return {} as ISidebarMenu;
}

export interface ISidebarMenu {
  filteredMenu: SidebarMenuItem[],
  borrowerAgentMenuList: SidebarMenuItem[],
  creditOpsMenuList: SidebarMenuItem[],
  financeAdminMenuList: SidebarMenuItem[],
}

const useSidebarMenu = () => {
  const { whitelistedProducts } = useUserProfile();
  const location = useLocation();
  const history = useHistory();
  const { params, isExact } = {
    ...matchPath<{
      productId?: string;
    }>(location.pathname, { path: ProductRoutesBasePath }),
  };
  const productId = params?.productId?.toUpperCase() || '';

  const roles = whitelistedProducts.find(((p) => p.productId === productId))?.roles || [];

  const sidebarMenu = getSidebarMenu(productId, roles);
  useEffect(() => {
    if (isExact) {
      if (isRoleBorrowerAgent(roles)) {
        history.replace(sidebarMenu.borrowerAgentMenuList[0].route);
      } else if (isRoleFinanceAgent(roles)) {
        history.replace(sidebarMenu.financeAdminMenuList[0].route);
      } else if (isRoleOpsAgent(roles)) {
        history.replace(sidebarMenu.creditOpsMenuList[0].route);
      }
    }
  }, [roles]);

  function isRoutePermitted(menuItems: SidebarMenuItem[]) {
    return (matchPath(location.pathname, { path: rootPath })?.isExact
        || menuItems.find((i) => matchPath(location.pathname, { path: i.route })?.isExact));
  }

  function isRoleBorrowerAgentAndRoutePermitted(): boolean {
    return Boolean(isRoleBorrowerAgent(roles)
        && isRoutePermitted(sidebarMenu.borrowerAgentMenuList));
  }

  function isRoleOpsAgentAndRoutePermitted(): boolean {
    return Boolean(isRoleOpsAgent(roles)
        && isRoutePermitted(sidebarMenu.creditOpsMenuList));
  }

  function isRoleFinanceAgentAndRoutePermitted(): boolean {
    return Boolean(isRoleFinanceAgent(roles)
        && isRoutePermitted(sidebarMenu.financeAdminMenuList));
  }

  function isAllowed() {
    return (isRoleBorrowerAgentAndRoutePermitted()
        || isRoleOpsAgentAndRoutePermitted()
        || isRoleFinanceAgentAndRoutePermitted());
  }

  return {
    items: sidebarMenu.filteredMenu,
    isAllowed,
  };
};

export default useSidebarMenu;
